import { createSlice } from '@repo/store';

/**
 * Web auth slice state
 * Similar to mobile auth slice but for web platform
 */
type AuthNamespaceState = {
  isLoggedIn: boolean;
  token: string | null;
  customToken: string | null; // Store custom token for session_cookie API calls
  sessionHydrated: boolean;
  // Absolute session expiry timestamp in seconds since epoch
  sessionExpiresAt: number | null;
  // TTL (seconds) returned by /profile/session_cookie/ for the current session cookie
  sessionExpiresInSeconds: number | null;
};

/**
 * Public interface for the web auth slice
 */
export type WebAuthSlice = AuthNamespaceState & {
  setLoggedIn: (payload: { token: string; customToken?: string }) => void;
  updateToken: (token: string) => void;
  logout: () => void;
  /**
   * Clear auth state without broadcasting to other tabs.
   * IMPORTANT: Use this only when reacting to an already-broadcast logout event.
   */
  logoutSilently: () => void;
  setSessionHydrated: (hydrated: boolean) => void;
  setSessionExpiresStamp: (expiresInSeconds: number) => void;
};

const initialState: AuthNamespaceState = {
  isLoggedIn: false,
  token: null,
  customToken: null,
  sessionHydrated: false,
  sessionExpiresAt: null,
  sessionExpiresInSeconds: null,
};

export const webAuthSlice = createSlice({
  name: 'webAuth',
  initialState,
  // Persist only the custom token; "logged in" is derived from a verified session/profile.
  // This prevents stale isLoggedIn=true after refresh when cookies are missing/invalid.
  persist: { keys: ['customToken', 'isLoggedIn', 'sessionExpiresAt', 'sessionExpiresInSeconds'] },
  actions: ({ setSlice }) => ({
    /**
     * Set user as logged in with token
     * Firebase handles the actual session management and token refresh
     * IMPORTANT: This replaces any existing token to prevent using stale tokens
     */
    setLoggedIn: (payload: { token: string; customToken?: string }) => {
      setSlice(prev => ({
        ...prev,
        isLoggedIn: true,
        token: payload.token,
        customToken: payload.customToken ?? prev.customToken ?? null,
        sessionHydrated: true,
      }));
    },

    /**
     * Update auth token
     * Used when Firebase refreshes the token
     * Also sets isLoggedIn to true if token is provided (user is authenticated)
     */
    updateToken: (token: string) => {
      setSlice(prev => ({
        ...prev,
        token,
        isLoggedIn: true,
        sessionHydrated: true,
      }));
    },

    /**
     * Clear auth state on logout
     * Note: Firebase sign out is handled in the logout flow (see profile/page.tsx)
     */
    logout: () => {
      setSlice({
        isLoggedIn: false,
        token: null,
        customToken: null,
        sessionHydrated: true,
        sessionExpiresAt: null,
        sessionExpiresInSeconds: null,
      });
    },

    logoutSilently: () => {
      setSlice({
        isLoggedIn: false,
        token: null,
        customToken: null,
        sessionHydrated: true,
        sessionExpiresAt: null,
        sessionExpiresInSeconds: null,
      });
    },

    setSessionHydrated: (hydrated: boolean) => {
      setSlice(prev => ({
        ...prev,
        sessionHydrated: hydrated,
      }));
    },

    setSessionExpiresStamp: (expiresInSeconds: number) => {
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresAt = nowSec + expiresInSeconds;
      setSlice(prev => ({
        ...prev,
        sessionExpiresAt: Number.isFinite(expiresAt) ? expiresAt : (prev.sessionExpiresAt ?? null),
        sessionExpiresInSeconds: Number.isFinite(expiresInSeconds)
          ? expiresInSeconds
          : (prev.sessionExpiresInSeconds ?? null),
      }));
    },
  }),
});
